<script setup>
/* eslint-disable no-use-before-define */
import { computed, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import TalkviaManualSimulatorsAPI from 'dashboard/api/talkviaManualSimulators';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const productTypes = [
  'FGTS',
  'CLT',
  'INSS',
  'Consignado',
  'Crédito pessoal',
  'Portabilidade',
  'Refinanciamento',
];

const simulators = ref([]);
const selectedSimulatorId = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const filter = ref('');
const bankOptions = ref([]);
const showBankOptions = ref(false);
const isLoadingBanks = ref(false);

const blankForm = () => ({
  bank_name: '',
  bank_code: '',
  name: '',
  code: '',
  product_type: 'FGTS',
  agreement_type: '',
  monthly_rate: '',
  annual_rate: '',
  coefficient: '',
  min_term: '',
  max_term: '',
  min_amount: '',
  max_amount: '',
  min_installment: '',
  max_installment: '',
  effective_from: new Date().toISOString().slice(0, 10),
  effective_until: '',
  active: true,
  priority: 0,
  rules: {
    min_age: '',
    max_age: '',
    allowed_ufs: '',
    blocked_ufs: '',
    notes: '',
  },
  metadata: {
    table_code: '',
    campaign: '',
    observation: '',
  },
});

const form = reactive(blankForm());

const productOptions = productTypes.map(type => ({ label: type, value: type }));

const selectedSimulator = computed(() =>
  simulators.value.find(simulator => simulator.id === selectedSimulatorId.value)
);

const filteredSimulators = computed(() => {
  const query = filter.value.trim().toLowerCase();
  if (!query) return simulators.value;
  return simulators.value.filter(simulator =>
    [
      simulator.bank_name,
      simulator.bank_code,
      simulator.name,
      simulator.product_type,
      simulator.agreement_type,
    ]
      .join(' ')
      .toLowerCase()
      .includes(query)
  );
});

const activeCount = computed(
  () => simulators.value.filter(simulator => simulator.active).length
);

const groupedByBank = computed(() =>
  filteredSimulators.value.reduce((groups, simulator) => {
    const bank = simulator.bank_name || 'Banco sem nome';
    groups[bank] ||= [];
    groups[bank].push(simulator);
    return groups;
  }, {})
);

const loadSimulators = async () => {
  isLoading.value = true;
  try {
    const { data } = await TalkviaManualSimulatorsAPI.get();
    simulators.value = data.simulators || [];
    if (!selectedSimulatorId.value && simulators.value.length) {
      assignForm(simulators.value[0]);
    }
  } catch {
    useAlert('Não foi possível carregar os simuladores.');
  } finally {
    isLoading.value = false;
  }
};

const assignForm = simulator => {
  Object.assign(form, blankForm(), {
    ...simulator,
    rules: { ...blankForm().rules, ...(simulator.rules || {}) },
    metadata: { ...blankForm().metadata, ...(simulator.metadata || {}) },
  });
  selectedSimulatorId.value = simulator.id;
};

const resetForm = () => {
  Object.assign(form, blankForm());
  selectedSimulatorId.value = null;
  bankOptions.value = [];
  showBankOptions.value = false;
};

const payload = () => ({
  ...form,
  monthly_rate: numberOrNull(form.monthly_rate),
  annual_rate: numberOrNull(form.annual_rate),
  coefficient: numberOrNull(form.coefficient),
  min_term: numberOrNull(form.min_term),
  max_term: numberOrNull(form.max_term),
  min_amount: numberOrNull(form.min_amount),
  max_amount: numberOrNull(form.max_amount),
  min_installment: numberOrNull(form.min_installment),
  max_installment: numberOrNull(form.max_installment),
  priority: Number(form.priority || 0),
  effective_until: form.effective_until || null,
  rules: {
    ...form.rules,
    min_age: numberOrNull(form.rules.min_age),
    max_age: numberOrNull(form.rules.max_age),
    allowed_ufs: splitList(form.rules.allowed_ufs),
    blocked_ufs: splitList(form.rules.blocked_ufs),
  },
});

const saveSimulator = async () => {
  if (!form.bank_name || !form.name || !form.coefficient) {
    useAlert('Informe banco, nome e coeficiente.');
    return;
  }

  isSaving.value = true;
  try {
    const response = selectedSimulatorId.value
      ? await TalkviaManualSimulatorsAPI.update(
          selectedSimulatorId.value,
          payload()
        )
      : await TalkviaManualSimulatorsAPI.create(payload());
    const saved = response.data;
    const index = simulators.value.findIndex(item => item.id === saved.id);
    if (index >= 0) simulators.value.splice(index, 1, saved);
    else simulators.value.unshift(saved);
    assignForm(saved);
    useAlert('Simulador salvo.');
  } catch {
    useAlert('Não foi possível salvar o simulador.');
  } finally {
    isSaving.value = false;
  }
};

const removeSimulator = async simulator => {
  try {
    await TalkviaManualSimulatorsAPI.delete(simulator.id);
    simulators.value = simulators.value.filter(
      item => item.id !== simulator.id
    );
    if (selectedSimulatorId.value === simulator.id) resetForm();
    useAlert('Simulador removido.');
  } catch {
    useAlert('Não foi possível remover o simulador.');
  }
};

const searchBanks = async () => {
  const query = form.bank_name.trim();
  if (query.length < 2) {
    bankOptions.value = [];
    showBankOptions.value = false;
    return;
  }
  isLoadingBanks.value = true;
  try {
    const { data } = await TalkviaManualSimulatorsAPI.banks({ q: query });
    bankOptions.value = data.banks || [];
    showBankOptions.value = bankOptions.value.length > 0;
  } catch {
    bankOptions.value = [];
    showBankOptions.value = false;
  } finally {
    isLoadingBanks.value = false;
  }
};

const onBankInput = () => {
  form.bank_code = '';
  searchBanks();
};

const selectBank = bank => {
  form.bank_name = bank.name;
  form.bank_code = bank.code;
  bankOptions.value = [];
  showBankOptions.value = false;
};

const numberOrNull = value => {
  if (value === '' || value === null || value === undefined) return null;
  return Number(value);
};

const splitList = value => {
  if (Array.isArray(value)) return value;
  return String(value || '')
    .split(',')
    .map(item => item.trim().toUpperCase())
    .filter(Boolean);
};

const formatPercent = value => {
  if (value === null || value === undefined || value === '') return '-';
  return `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 6 })}%`;
};

onMounted(loadSimulators);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline, vue/max-attributes-per-line -->
  <main
    class="flex h-full min-h-0 flex-col overflow-auto bg-n-background p-6 text-n-slate-12"
  >
    <header
      class="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between"
    >
      <div class="max-w-3xl">
        <div
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-brand"
        >
          Simulação manual
        </div>
        <h1 class="m-0 text-3xl font-semibold">Simuladores por banco</h1>
        <p class="mb-0 mt-2 text-base leading-7 text-n-slate-11">
          Cadastre tabelas por banco, convênio, taxa, coeficiente e vigência
          para simular propostas manualmente.
        </p>
      </div>
      <div
        class="grid grid-cols-3 gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-3"
      >
        <div>
          <div class="text-2xl font-semibold">{{ simulators.length }}</div>
          <div class="text-xs text-n-slate-10">Simuladores</div>
        </div>
        <div>
          <div class="text-2xl font-semibold">{{ activeCount }}</div>
          <div class="text-xs text-n-slate-10">Ativos</div>
        </div>
        <div>
          <div class="text-2xl font-semibold">
            {{ Object.keys(groupedByBank).length }}
          </div>
          <div class="text-xs text-n-slate-10">Bancos</div>
        </div>
      </div>
    </header>

    <section class="grid min-h-0 gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
      <aside
        class="flex min-h-0 flex-col rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div class="border-b border-n-weak p-4">
          <div class="mb-3 flex items-center justify-between gap-3">
            <h2 class="m-0 text-base font-semibold">Simuladores</h2>
            <Button
              label="Novo"
              icon="i-lucide-plus"
              size="sm"
              @click="resetForm"
            />
          </div>
          <Input
            v-model="filter"
            placeholder="Buscar banco, convênio ou produto"
            type="search"
          />
        </div>
        <div v-if="isLoading" class="p-4 text-sm text-n-slate-11">
          Carregando simuladores...
        </div>
        <div v-else-if="!simulators.length" class="p-4 text-sm text-n-slate-11">
          Nenhum simulador cadastrado.
        </div>
        <div v-else class="min-h-0 flex-1 overflow-auto p-3">
          <section
            v-for="(items, bank) in groupedByBank"
            :key="bank"
            class="mb-4"
          >
            <h3
              class="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ bank }}
            </h3>
            <Button
              v-for="simulator in items"
              :key="simulator.id"
              type="button"
              color="slate"
              variant="ghost"
              justify="start"
              class="mb-2 !h-auto w-full !items-stretch !gap-0 !rounded-lg border !p-3 text-left"
              :class="
                selectedSimulatorId === simulator.id
                  ? 'border-n-brand bg-n-brand/10'
                  : 'border-n-weak bg-n-solid-2 hover:border-n-slate-6'
              "
              @click="assignForm(simulator)"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="truncate text-sm font-semibold">
                    {{ simulator.name }}
                  </div>
                  <div class="mt-1 text-xs text-n-slate-10">
                    {{ simulator.product_type }} ·
                    {{ simulator.agreement_type || 'Sem convênio' }}
                  </div>
                </div>
                <span
                  class="rounded-full px-2 py-1 text-[11px] font-semibold"
                  :class="
                    simulator.active
                      ? 'bg-emerald-500/10 text-emerald-600'
                      : 'bg-n-slate-5 text-n-slate-10'
                  "
                  >{{ simulator.active ? 'Ativo' : 'Inativo' }}</span
                >
              </div>
              <p class="mb-0 mt-2 line-clamp-2 text-xs text-n-slate-11">
                {{ simulator.simulation_summary || 'Sem resumo' }}
              </p>
            </Button>
          </section>
        </div>
      </aside>

      <div class="grid min-w-0 gap-5 2xl:grid-cols-[minmax(0,1fr)_360px]">
        <form
          class="min-w-0 rounded-xl border border-n-weak bg-n-solid-1 p-5"
          @submit.prevent="saveSimulator"
        >
          <div
            class="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
          >
            <div>
              <h2 class="m-0 text-xl font-semibold">
                {{
                  selectedSimulator ? 'Editar simulador' : 'Cadastrar simulador'
                }}
              </h2>
              <p class="mb-0 mt-1 text-sm text-n-slate-11">
                Mantenha coeficientes, faixas e vigências fáceis de revisar.
              </p>
            </div>
            <div class="flex gap-2">
              <Button
                v-if="selectedSimulator"
                type="button"
                label="Cancelar"
                color="slate"
                variant="outline"
                @click="resetForm"
              />
              <Button
                type="submit"
                :label="selectedSimulator ? 'Atualizar' : 'Salvar'"
                :is-loading="isSaving"
                :disabled="isSaving"
              />
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div class="relative xl:col-span-2">
              <div class="relative">
                <Input
                  v-model="form.bank_name"
                  custom-input-class="!pr-24"
                  label="Banco"
                  placeholder="Código ou nome do banco"
                  @focus="searchBanks"
                  @input="onBankInput"
                />
                <span
                  v-if="form.bank_code"
                  class="absolute bottom-2 right-3 rounded-md bg-n-brand/10 px-2 py-1 text-xs font-semibold text-n-brand"
                  >{{ form.bank_code }}</span
                >
              </div>
              <div
                v-if="showBankOptions"
                class="absolute left-0 right-0 top-full z-20 mt-2 max-h-60 overflow-auto rounded-xl border border-n-weak bg-n-solid-2 p-2 shadow-xl"
              >
                <Button
                  v-for="bank in bankOptions"
                  :key="bank.code"
                  type="button"
                  color="slate"
                  variant="ghost"
                  justify="start"
                  class="!h-auto w-full !justify-between !px-3 !py-2 text-left"
                  @click="selectBank(bank)"
                >
                  <span class="font-medium text-n-slate-12">{{
                    bank.name
                  }}</span>
                  <span
                    class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs font-semibold text-n-slate-11"
                    >{{ bank.code }}</span
                  >
                </Button>
              </div>
              <p class="mb-0 mt-1 text-xs text-n-slate-10">
                {{
                  isLoadingBanks
                    ? 'Buscando bancos...'
                    : 'Pesquise por código ou nome.'
                }}
              </p>
            </div>
            <Input
              v-model="form.name"
              label="Nome"
              placeholder="FGTS tabela junho"
            />
            <Input
              v-model="form.code"
              label="Código interno"
              placeholder="auto ao salvar"
            />
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                Produto
              </label>
              <Select
                v-model="form.product_type"
                :options="productOptions"
                class="w-full [&>select]:w-full"
              />
            </div>
            <Input
              v-model="form.agreement_type"
              label="Convênio"
              placeholder="INSS, SIAPE, CLT privado"
            />
            <Input
              v-model="form.metadata.table_code"
              label="Código tabela"
              placeholder="Ex.: 00123"
            />
            <Input v-model="form.priority" label="Prioridade" type="number" />
          </div>

          <div class="mt-6 rounded-xl border border-n-weak p-4">
            <h3 class="m-0 text-base font-semibold">Coeficiente e limites</h3>
            <div class="mt-4 grid gap-4 md:grid-cols-3 xl:grid-cols-6">
              <Input
                v-model="form.monthly_rate"
                label="Taxa a.m. %"
                type="number"
                step="0.000001"
              />
              <Input
                v-model="form.annual_rate"
                label="Taxa a.a. %"
                type="number"
                step="0.000001"
              />
              <Input
                v-model="form.coefficient"
                label="Coeficiente"
                type="number"
                step="0.0000000001"
              />
              <Input v-model="form.min_term" label="Prazo mín." type="number" />
              <Input v-model="form.max_term" label="Prazo máx." type="number" />
              <label
                class="flex items-center gap-2 pt-7 text-sm font-medium text-n-slate-12"
              >
                <Checkbox v-model="form.active" />
                Ativo
              </label>
              <Input
                v-model="form.min_amount"
                label="Valor mín."
                type="number"
                step="0.01"
              />
              <Input
                v-model="form.max_amount"
                label="Valor máx."
                type="number"
                step="0.01"
              />
              <Input
                v-model="form.min_installment"
                label="Parcela mín."
                type="number"
                step="0.01"
              />
              <Input
                v-model="form.max_installment"
                label="Parcela máx."
                type="number"
                step="0.01"
              />
              <Input
                v-model="form.effective_from"
                label="Vigência início"
                type="date"
              />
              <Input
                v-model="form.effective_until"
                label="Vigência fim"
                type="date"
              />
            </div>
          </div>

          <div class="mt-6 rounded-xl border border-n-weak p-4">
            <h3 class="m-0 text-base font-semibold">Regras complementares</h3>
            <div class="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Input
                v-model="form.rules.min_age"
                label="Idade mín."
                type="number"
              />
              <Input
                v-model="form.rules.max_age"
                label="Idade máx."
                type="number"
              />
              <Input
                v-model="form.rules.allowed_ufs"
                label="UFs permitidas"
                placeholder="SP, RJ, MG"
              />
              <Input
                v-model="form.rules.blocked_ufs"
                label="UFs bloqueadas"
                placeholder="AM, RR"
              />
              <Input
                v-model="form.metadata.campaign"
                class="xl:col-span-2"
                label="Campanha"
                placeholder="Nome comercial da tabela"
              />
              <Input
                v-model="form.rules.notes"
                class="xl:col-span-2"
                label="Observações"
                placeholder="Exceções, documentação, regra operacional"
              />
            </div>
          </div>
        </form>

        <aside class="min-w-0 space-y-5">
          <section class="rounded-xl border border-n-weak bg-n-solid-1 p-5">
            <h2 class="m-0 text-lg font-semibold">Resumo</h2>
            <dl class="mt-4 space-y-3 text-sm">
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-10">Banco</dt>
                <dd class="text-right font-medium">
                  {{ form.bank_code ? `${form.bank_code} · ` : ''
                  }}{{ form.bank_name || '-' }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-10">Produto</dt>
                <dd class="text-right font-medium">{{ form.product_type }}</dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-10">Taxa</dt>
                <dd class="text-right font-medium">
                  {{ formatPercent(form.monthly_rate) }} a.m.
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-10">Coeficiente</dt>
                <dd class="text-right font-medium">
                  {{ form.coefficient || '-' }}
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-10">Prazo</dt>
                <dd class="text-right font-medium">
                  {{ form.min_term || '-' }} a {{ form.max_term || '-' }}x
                </dd>
              </div>
              <div class="flex justify-between gap-4">
                <dt class="text-n-slate-10">Vigência</dt>
                <dd class="text-right font-medium">
                  {{ form.effective_from || '-' }} até
                  {{ form.effective_until || 'sem fim' }}
                </dd>
              </div>
            </dl>
            <Button
              v-if="selectedSimulator"
              type="button"
              class="mt-5 w-full"
              label="Remover simulador"
              icon="i-lucide-trash-2"
              color="ruby"
              variant="outline"
              @click="removeSimulator(selectedSimulator)"
            />
          </section>
        </aside>
      </div>
    </section>
  </main>
</template>
